import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { DrawerForm } from "../Common/styles/drawer";
import http from "../../utils/http";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import { Slot } from "../../Redux/reducers/appointmentSlice";

const schema = yup.object().shape({
  date: yup.string().required("Date is required"),
  newSlotId: yup.string().required("Please select a time slot"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  slotData: Slot;
  roomId: string;
  refreshPage: () => void;
};

const RescheduleAppointmentModal = ({
  showModal,
  closeModal,
  slotData,
  roomId,
  refreshPage,
}: Props) => {
  const { id: studyId } = useParams();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);

  const fetchAvailableSlots = async (date: string) => {
    if (!roomId) {
      setAvailableSlots([]);
      return;
    }

    try {
      setSlotsLoading(true);
      const selectedDate = DateTime.fromFormat(date, "MM/dd/yyyy");
      const startDate = selectedDate.startOf("day");
      const endDate = selectedDate.endOf("day");

      const response = await http.get(
        `/admin/study/${studyId}/appointments/room/${roomId}?startDate=${startDate
          .toUTC()
          .toISO()}&endDate=${endDate.toUTC().toISO()}`
      );

      const currentTime = DateTime.now();

      // Filter to show only empty slots (no appointment booked)
      const emptySlots: Slot[] = response.data.data
        .filter((slot: any) => {
          const slotEndTime = DateTime.fromISO(slot.endDateTime);

          return !slot.appointmentId && slotEndTime >= currentTime;
        })
        .map((slot: any) => ({
          id: slot.id,
          appointmentId: slot.appointmentId,
          startDateTime: new Date(slot.startDateTime),
          endDateTime: new Date(slot.endDateTime),
          status: slot.status,
          date: DateTime.fromISO(slot.startDateTime).toFormat("MM/dd/yyyy"),
          time:
            DateTime.fromISO(slot.startDateTime).toFormat("hh:mm a") +
            " - " +
            DateTime.fromISO(slot.endDateTime).toFormat("hh:mm a"),
        }));

      setAvailableSlots(emptySlots);
    } catch (error) {
      errorToastMessage(error as Error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const selectedSlot = availableSlots.find(
        (slot) => slot.id === values.newSlotId
      );

      if (!selectedSlot) {
        errorToastMessage(
          new Error(
            "Selected slot is no longer available. Please select another slot."
          )
        );
        setSubmitLoader(false);
        return;
      }

      const currentTime = DateTime.now();
      const slotEndTime = DateTime.fromJSDate(selectedSlot.endDateTime);

      if (slotEndTime <= currentTime) {
        errorToastMessage(
          new Error(
            "The selected time slot has passed. Please select a future time slot."
          )
        );
        if (values.date) {
          fetchAvailableSlots(values.date);
        }
        setSubmitLoader(false);
        return;
      }
      const payload = {
        newSlotId: values.newSlotId,
      };

      const res = await http.post(
        `/admin/study/${studyId}/appointments/${slotData.id}/reschedule`,
        payload
      );

      toastMessage(
        "success",
        res.data.message || "Appointment rescheduled successfully"
      );
      refreshPage();
      closeModal();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Reschedule Appointment" onCloseClick={closeModal} />

        <Formik
          initialValues={{
            date: "",
            newSlotId: "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box sx={{ flex: 1 }}>
                {/* Current Appointment Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Current Appointment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {slotData?.date} at {slotData?.time}
                  </Typography>
                </Box>

                {/* Date Picker */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Select New Date <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <DatePicker
                    disablePast
                    format="MM/dd/yyyy"
                    value={
                      values.date
                        ? DateTime.fromFormat(values.date, "MM/dd/yyyy")
                        : null
                    }
                    onChange={(newValue: any) => {
                      if (newValue) {
                        const formattedDate = newValue.toFormat("MM/dd/yyyy");
                        setFieldValue("date", formattedDate);
                        setFieldValue("newSlotId", "");
                        fetchAvailableSlots(formattedDate);
                      }
                    }}
                    slotProps={{
                      textField: {
                        inputProps: {
                          readOnly: true,
                          placeholder: "Select date",
                          id: "date",
                        },
                        error: touched.date && Boolean(errors.date),
                        helperText:
                          touched.date && errors.date
                            ? (errors.date as string)
                            : " ",
                      },
                    }}
                  />
                </FormControl>

                {/* Time Slot Dropdown */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Select Time Slot <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    value={values.newSlotId}
                    onChange={(e) => setFieldValue("newSlotId", e.target.value)}
                    disabled={!values.date || slotsLoading}
                    displayEmpty
                    error={touched.newSlotId && Boolean(errors.newSlotId)}
                  >
                    <MenuItem value="" disabled>
                      {slotsLoading
                        ? "Loading slots..."
                        : !values.date
                        ? "Please select a date first"
                        : availableSlots.length === 0
                        ? "No available slots for this date"
                        : "Select a time slot"}
                    </MenuItem>
                    {availableSlots.map((slot) => (
                      <MenuItem key={slot.id} value={slot.id}>
                        {slot.time}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.newSlotId && errors.newSlotId && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {errors.newSlotId as string}
                    </Typography>
                  )}
                  {slotsLoading && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </FormControl>
              </Box>

              <Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={ModalDrawerButtonStyles}>
                  <Button variant="outlined" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitLoader || !values.newSlotId}
                  >
                    {submitLoader ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Reschedule Appointment"
                    )}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default RescheduleAppointmentModal;
